import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface RiskLimitItem {
  id: number;
  riskLevel: string;
  operationType: string;
  amount: string;
  currency: string;
  isActive: boolean;
  updatedAt: string;
}

interface RiskLimitsState {
  list: RiskLimitItem[];
  isListFetched: boolean;
  isLoading: boolean;
  payload?: RiskLimitItem;
  isItemFetching: boolean;
  processingItemId?: number;
}

const initialState: RiskLimitsState = {
  list: [],
  isListFetched: false,
  isLoading: false,
  payload: undefined,
  isItemFetching: false,
  processingItemId: undefined,
};

export const getRiskLimitsList = createAsyncThunk(
  "riskLimits/list",
  async () => {
    try {
      const riskLevelsResponse = await instance.get(
        apiRoutes.riskPermissionsList,
      );

      const riskLevels = riskLevelsResponse.data?.items || [];

      const requests = riskLevels.map((item: any) =>
        instance.get(`${apiRoutes.riskLimitsList}/${item.riskLevel}`),
      );

      const responses = await Promise.all(requests);

      const limits = responses.flatMap((response: any) => response.data.items);

      return limits;
    } catch (error) {
      return undefined;
    }
  },
);

export const getRiskLimitItem = createAsyncThunk(
  "riskLimits/item",
  async ({
    riskLevel,
    operationType,
    currency,
  }: {
    riskLevel: string;
    operationType: string;
    currency: string;
  }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.riskLimitsList}/${riskLevel}`,
        {
          params: {
            operationType,
            currency,
          },
        },
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const updateRiskLimit = createAsyncThunk<
  boolean,
  {
    riskLevel: string;
    operationType: string;
    currency: string;
    data: any;
  },
  { rejectValue: ApiErrorPayload }
>(
  "riskLimits/update",
  async ({ riskLevel, operationType, currency, data }, { rejectWithValue }) => {
    try {
      await instance.put(`${apiRoutes.riskLimitsUpdate}/${riskLevel}`, data, {
        params: {
          operationType,
          currency,
        },
      });

      return true;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const riskLimitsSlice = createSlice({
  name: "riskLimits",
  initialState,
  reducers: {
    handleUpdateFields: (
      state,
      action: PayloadAction<Partial<RiskLimitItem>>,
    ) => {
      state.payload = {
        ...state.payload,
        ...action.payload,
      } as RiskLimitItem;
    },

    handleClearFields: (state) => {
      state.payload = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRiskLimitsList.pending, (state) => {
      state.isLoading = true;
      state.isListFetched = false;
    });

    builder.addCase(getRiskLimitsList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isListFetched = true;

      state.list =
        action.payload?.map((item: any, index: number) => {
          const amount = item.amountMinor / Math.pow(10, item.currencyScale);

          return {
            id: index + 1,
            riskLevel: item.riskLevel,
            operationType: item.operationType,
            amount: amount.toLocaleString(undefined, {
              minimumFractionDigits: item.currencyScale,
              maximumFractionDigits: item.currencyScale,
            }),
            currency: item.currency,
            isActive: item.isActive,
            updatedAt: item.updatedAt,
          };
        }) ?? [];
    });

    builder.addCase(getRiskLimitsList.rejected, (state) => {
      state.isLoading = false;
      state.isListFetched = true;
    });

    builder.addCase(getRiskLimitItem.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(getRiskLimitItem.fulfilled, (state, action) => {
      state.isItemFetching = false;

      const item = action.payload?.items?.[0];

      if (!item) return;

      const amount = item.amountMinor / Math.pow(10, item.currencyScale);

      state.payload = {
        id: 0,
        riskLevel: item.riskLevel,
        operationType: item.operationType,
        amount: amount.toString(),
        currency: item.currency,
        isActive: item.isActive,
        updatedAt: item.updatedAt,
      };
    });
    builder.addCase(getRiskLimitItem.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateRiskLimit.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = state.list.find(
        (item) =>
          item.riskLevel === action.meta.arg.riskLevel &&
          item.operationType === action.meta.arg.operationType &&
          item.currency === action.meta.arg.currency,
      )?.id;
    });

    builder.addCase(updateRiskLimit.fulfilled, (state, action) => {
      state.isItemFetching = false;
      const { riskLevel, operationType, currency, data } = action.meta.arg;
      state.list = state.list.map((item) =>
        item.riskLevel === riskLevel &&
        item.operationType === operationType &&
        item.currency === currency
          ? mergeExistingFields(item, {
              amount: String(data.amount),
              isActive: Boolean(data.isActive),
            })
          : item,
      );
      state.processingItemId = undefined;
    });

    builder.addCase(updateRiskLimit.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
  },
});

export const { handleUpdateFields, handleClearFields } =
  riskLimitsSlice.actions;

export default riskLimitsSlice.reducer;
